import Vue from 'vue';
import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import {
  findPendingMessageIndex,
  getConversationActivityAt,
  getLatestTimestamp,
  isPendingMessage,
  shouldKeepExistingMessage,
} from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { debounce } from '@chatwoot/utils';

const LOG_PREFIX = '[CONVERSATION_MUTATION]';

const logMutation = (mutation, details = '') => {
  if (details) {
    console.debug(`${LOG_PREFIX} ${mutation} | ${details}`);
    return;
  }

  console.debug(`${LOG_PREFIX} ${mutation}`);
};

const logMutationResult = (mutation, conversationId, action, details = '') => {
  const message = `${LOG_PREFIX} ${mutation} | #${conversationId} | ${action}`;

  if (details) {
    console.debug(`${message} | ${details}`);
    return;
  }

  console.debug(message);
};

const state = {
  allConversations: [],
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  campaignFilter: 'all',
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  listLoadingByGroupedBoard: {},
  messageUiFlags: {    
    isCreating: false,
  },
  conversationListNextRefreshAt: null,
};

const keepLatestActivity = (chat, activityAt) => {
  ['last_activity_at', 'timestamp'].forEach(attribute => {
    const latestTimestamp = getLatestTimestamp(chat[attribute], activityAt);

    if (latestTimestamp !== undefined) {
      Vue.set(chat, attribute, latestTimestamp);
    }
  });
};

const keepLatestNonActivityMessage = (chat, message) => {
  const current = chat.last_non_activity_message;

  if (message && (!current || message.created_at > current.created_at)) {
    Vue.set(chat, 'last_non_activity_message', message);
  }
};

const emitFetchAllConversation = debounce(
  () => {
    window.bus.$emit('fetch_all_conversation');
  },
  1000 * 60 * 2
);

// mutations
export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, { conversationList, forceReset = false }) {
    logMutation(
      'SET_ALL_CONVERSATION',
      `count=${conversationList.length} forceReset=${forceReset}`
    );

    let allConversations = [..._state.allConversations];

    if (forceReset) {
      const [chat] = getSelectedChatConversation(_state);
      allConversations = chat ? [chat] : [];
    }

    const newAllConversations = [...allConversations];

    conversationList.forEach(conversation => {
      const existingConversation = newAllConversations.find(
        c => c.id === conversation.id
      );

      if (existingConversation) {
        keepLatestActivity(
          existingConversation,
          getConversationActivityAt(conversation)
        );
        keepLatestNonActivityMessage(
          existingConversation,
          conversation.last_non_activity_message
        );

        return;
      }

      newAllConversations.push(conversation);

      logMutationResult('SET_ALL_CONVERSATION', conversation.id, 'ADDED');
    });

    _state.allConversations = newAllConversations;
  },

  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;

    logMutation('EMPTY_ALL_CONVERSATION');
  },

  [types.SET_ALL_MESSAGES_LOADED](_state, { id } = {}) {
    const chat = _state.allConversations.find(c => c.id === id);

    if (chat) {
      Vue.set(chat, 'allMessagesLoaded', true);
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, { id } = {}) {
    const chat = _state.allConversations.find(c => c.id === id);

    if (chat) {
      Vue.set(chat, 'allMessagesLoaded', false);
    }
  },

  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data.length) {
      return;
    }

    const [chat] = _state.allConversations.filter(c => c.id === id);

    if (chat) {
      chat.messages.unshift(...data);
    }
  },

  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);

    if (!chat) {
      return;
    }

    if (!chat?.attachments) {
      Vue.set(chat, 'attachments', []);
    }

    Vue.set(chat, 'attachments', []);
    chat.attachments.push(...data);
  },

  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);

    if (!chat) {
      return;
    }

    Vue.set(chat, 'messages', data);
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    const [chat] = getSelectedChatConversation(_state);

    if (chat) {
      Vue.set(chat.meta, 'assignee', assignee);
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);

    if (!chat) {
      return;
    }

    Vue.set(chat.meta, 'team', team);

    logMutationResult(
      'ASSIGN_TEAM',
      conversationId,
      'UPDATED',
      `team=#${team?.id || 'null'}`
    );
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);

    if (chat) {
      keepLatestActivity(chat, lastActivityAt);
    }
  },

  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);

    if (chat) {
      Vue.set(chat, 'priority', priority);
    }
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chat] = getSelectedChatConversation(_state);

    if (chat) {
      Vue.set(chat, 'custom_attributes', custom_attributes);
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};

    Vue.set(conversation, 'snoozed_until', snoozedUntil);
    Vue.set(conversation, 'status', status);

    logMutationResult(
      'CHANGE_CONVERSATION_STATUS',
      conversationId,
      'UPDATED',
      `status=${status}`
    );
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);

    if (chat) {
      Vue.set(chat, 'muted', true);
    }
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);

    if (chat) {
      Vue.set(chat, 'muted', false);
    }
  },

  [types.ADD_CONVERSATION_ATTACHMENTS]({ allConversations }, message) {
    const { conversation_id: conversationId } = message;

    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    if (!chat) {
      return;
    }

    if (!chat?.attachments) {
      Vue.set(chat, 'attachments', []);
    }

    const isMessageSent =
      message.status === MESSAGE_STATUS.SENT && message.attachments;

    if (isMessageSent) {
      message.attachments.forEach(attachment => {
        const exists = chat.attachments.some(a => a.id === attachment.id);

        if (!exists) {
          chat.attachments.push(attachment);
        }
      });
    }
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS]({ allConversations }, message) {
    const { conversation_id: conversationId } = message;

    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    if (!chat || !chat?.attachments) {
      return;
    }

    const isMessageSent = message.status === MESSAGE_STATUS.SENT;

    if (isMessageSent) {
      const attachmentIndex = chat.attachments.findIndex(
        a => a.message_id === message.id
      );

      if (attachmentIndex !== -1) {
        chat.attachments.splice(attachmentIndex, 1);
      }
    }
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;

    const [item] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });

    let chat = item;

    if (!chat) {
      emitFetchAllConversation();

      const [refetchedItem] = getSelectedChatConversation({
        allConversations,
        selectedChatId: conversationId
      });

      chat = refetchedItem;
    }

    if (!chat) {
      logMutationResult(
        'ADD_MESSAGE',
        conversationId,
        'SKIPPED',
        'conversation_not_found'
      );

      return;
    }

    const pendingMessageIndex = findPendingMessageIndex(chat, message);

    if (pendingMessageIndex !== -1) {
      const messageInStore = chat.messages[pendingMessageIndex];

      if (shouldKeepExistingMessage(messageInStore, message)) {
        return;
      }

      Vue.set(chat.messages, pendingMessageIndex, message);

      if (isPendingMessage(messageInStore) && !isPendingMessage(message)) {
        keepLatestActivity(chat, message.created_at);
      }

      return;
    }

    const hasNewerMessageInStore =
      !isPendingMessage(message) &&
      chat.messages.some(storedMessage => storedMessage.id > message.id);

    chat.messages.push(message);

    if (!isPendingMessage(message)) {
      keepLatestActivity(chat, message.created_at);
    }

    const { conversation: { unread_count: unreadCount = 0 } = {} } = message;

    if (!hasNewerMessageInStore) {
      chat.unread_count = unreadCount;
    }

    if (selectedChatId === conversationId) {
      window.bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const { appliedFilters } = _state;

    if (appliedFilters.length === 0 && !_state.allConversations.some(c => c.id === conversation.id)) {
      _state.allConversations.push(conversation);

      logMutationResult(
        'ADD_CONVERSATION',
        conversation.id,
        'ADDED'
      );
    }
  },

  [types.REMOVE_CONVERSATION](_state, conversation) {
    const index = _state.allConversations.findIndex(
      c => c.id === conversation.id
    );

    if (index === -1) {
      logMutationResult(
        'REMOVE_CONVERSATION',
        conversation.id,
        'SKIPPED',
        'not_found'
      );

      return;
    }

    _state.allConversations.splice(index, 1);

    if (_state.selectedChatId === conversation.id) {
      _state.selectedChatId = null;
    }

    logMutationResult(
      'REMOVE_CONVERSATION',
      conversation.id,
      'REMOVED'
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations, appliedFilters } = _state;

    const currentConversationIndex = allConversations.findIndex(
      c => c.id === conversation.id
    );

    const activityAt = getConversationActivityAt(conversation);

    if (currentConversationIndex > -1) {
      const { messages, ...conversationAttributes } = conversation;
      const existingConversation = allConversations[currentConversationIndex];

      const currentConversation = {
        ...existingConversation,
        ...conversationAttributes,
      };

      keepLatestActivity(
        currentConversation,
        getLatestTimestamp(existingConversation.last_activity_at, activityAt)
      );

      Vue.set(allConversations, currentConversationIndex, currentConversation);

      if (_state.selectedChatId === conversation.id) {
        window.bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }

      logMutationResult(
        'UPDATE_CONVERSATION',
        conversation.id,
        'UPDATED'
      );

      return;
    }

    if (appliedFilters.length === 0) {
      keepLatestActivity(conversation, activityAt);
      _state.allConversations.push(conversation);

      logMutationResult(
        'UPDATE_CONVERSATION',
        conversation.id,
        'ADDED_TO_LIST'
      );
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  ['SET_LIST_LOADING_BY_GROUPED_BOARD'](_state, { groupedKey }) {
    _state.listLoadingByGroupedBoard[groupedKey] = true;
  },

  ['CLEAR_LIST_LOADING_BY_GROUPED_BOARD'](_state, { groupedKey }) {
    _state.listLoadingByGroupedBoard[groupedKey] = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);

    if (chat) {
      Vue.set(chat, 'agent_last_seen_at', lastSeen);
      Vue.set(chat, 'unread_count', unreadCount);
    }
  },

  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.SET_CAMPAIGN_FILTER](_state, filter) {
    _state.campaignFilter = filter;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  [types.UPDATE_ASSIGNEE](_state, payload) {
    const [chat] = _state.allConversations.filter(c => c.id === payload.id);

    if (chat) {
      Vue.set(chat.meta, 'assignee', payload.assignee);
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(
      c => c.id === conversationId
    );

    if (chat) {
      Vue.set(chat.meta, 'sender', payload);
    }
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(
      c => c.id === conversationId
    );

    if (chat) {
      Vue.set(chat, 'can_reply', canReply);
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );

    Vue.set(_state, 'allConversations', chats);
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },
  [types.SET_MESSAGE_UI_FLAG]($state, data) {
      $state.messageUiFlags = {
        ...$state.messageUiFlags,
        ...data,
      };
    },
  [types.SET_CONV_LIST_NEXT_REFRESH_AT]($state, nextRefreshAt) {
    $state.conversationListNextRefreshAt = nextRefreshAt;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
